// moved to repository

import { type Call, Device } from "@twilio/voice-sdk";
import cuid from "cuid";

import type { CallConfiguration, CallStatus } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("call");

import { eq, type InitialQueryBuilder } from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

import { createCall, createCallEvent } from "@storyboard/web-client";
import { transport } from "@/config";
import { AudioBoostProcessor } from "@/contexts/AudioBoostProcessor.ts";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import { createCallConfiguration, updateCallState } from "@/models/repositories/CallConfiguration";
import { rowsWhere, store } from "@/store/store";
import type { CallConfiguration as CallConfigurationRow, CallState } from "@/store/types";

export type CallConfigurationRecord = CallConfigurationRow;
export type CallConfigurationState = CallState;

const queuedCallConfigurationsQuery = (q: InitialQueryBuilder) =>
  q
    .from({ config: store.collection("callConfiguration") })
    .where(({ config }) => eq(config.state, "queued"))
    .orderBy(({ config }) => config.createdAt, { direction: "asc", nulls: "last" })
    .limit(2);

// Define the shape of our context
interface CallContextType {
  currentCall: Call | null;
  callTextStatus: string;
  callStartTime: number | null;
  createCallAndListen: (params: { fromAccountId: string; toAccountId: string; feedId: string }) => Promise<void>;
  listenForCalls: (callConfiguration: CallConfiguration) => Promise<void>;
  disconnectCall: (state?: CallConfigurationState, duration?: number) => void;
}

// Create the context with a default value
const CallContext = createContext<CallContextType | undefined>(undefined);

let currentCallConfiguration: CallConfigurationRecord | null = null;
let callStartTime: number | null = null;

function callStatusFromState(state: CallConfigurationState): CallStatus {
  switch (state) {
    case "accepted":
      return "inProgress";
    case "rejected":
      return "noAnswer";
    case "disconnected":
      return "completed";
    case "canceled":
      return "canceled";
    case "errored":
      return "failed";
    case "ringing":
      return "ringing";
    case "queued":
      return "queued";
    case "reconnecting":
      return "unknown";
    case "reconnected":
      return "unknown";
    case "timedout":
      return "timedout";
  }
}

const setCurrentCallConfigurationState = (state: CallConfigurationState, duration?: number) => {
  if (currentCallConfiguration) {
    updateCallState(currentCallConfiguration.id, state);
    if (!currentCallConfiguration.initiate) {
      const durationSeconds = callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : null;
      const callStatus = callStatusFromState(state);
      log.debug("[CALL] callStatus", { callStatus });
      switch (callStatus) {
        case "noAnswer":
          log.debug("[CALL] duration", { duration });
          void createCallEvent(transport, {
            callId: currentCallConfiguration.id,
            callStatus,
            durationSeconds: duration ?? 0,
          });
          break;
        case "completed":
        case "failed":
        case "canceled":
          void createCallEvent(transport, {
            callId: currentCallConfiguration.id,
            callStatus,
            durationSeconds,
          });
          break;
        case "inProgress":
        case "ringing":
          void createCallEvent(transport, { callId: currentCallConfiguration.id, callStatus });
          break;
        default:
          log.warn("unhandled call status", callStatus);
      }
    }
  }
};

// Provider component
export function CallProvider({ children }: { children: ReactNode }) {
  const [currentCall, setCurrentCall] = useState<Call | null>(null);
  const [callTextStatus, setCallTextStatus] = useState<string>("");
  const [device, setDevice] = useState<Device | null>(null);
  const { boostCallAudio } = useGlobalFlags();

  const callConfigurations = useLiveQuery(queuedCallConfigurationsQuery).data ?? [];

  const resetData = useCallback(() => {
    setCurrentCall(null);
    setDevice(null);
    setCallTextStatus(() => "");
    currentCallConfiguration = null;
    callStartTime = null;
  }, []);

  // Handle queued call configurations from appsync, which makes an outbound call
  useEffect(() => {
    const f = async () => {
      if (callConfigurations.length === 0) {
        return;
      }
      log.info("[CALL] Found queued call configurations", callConfigurations);
      if (currentCall) {
        log.info("[CALL] In active call. Rejecting queued call configurations", callConfigurations);
        await updateCallState(currentCallConfiguration.id, "rejected");
        return;
      }
      currentCallConfiguration = callConfigurations[0];
      log.info("[CALL] No active call. Accepting queued call configurations", callConfigurations);
      await updateCallState(currentCallConfiguration.id, "ringing");

      if (callConfigurations.length > 1) {
        // reject all queued except current
        store.batch(() => {
          for (const config of rowsWhere(
            "callConfiguration",
            (row) => row.state === "queued" && row.id !== currentCallConfiguration.id,
          )) {
            store.update("callConfiguration", config.id, (row) => ({ ...row, state: "rejected" }));
          }
        });
      }

      if (currentCallConfiguration.initiate) {
        await initializeCall();
      } else {
        await listenForCalls();
      }
    };
    f();
  }, [callConfigurations, boostCallAudio]);

  // Handle call acceptance
  const handleCallAccept = useCallback((...args: any[]) => {
    log.debug("[CALL] handleCallAccept", args);
    callStartTime = Date.now();
    setCurrentCallConfigurationState("accepted");
    setCallTextStatus(() => "accepted");
  }, []);

  // Handle call disconnection
  const handleCallDisconnect = useCallback((...args: any[]) => {
    log.debug("[CALL] handleCallDisconnect", args);
    setCurrentCallConfigurationState("disconnected");
    setCallTextStatus(() => "disconnected");
    resetData();
  }, []);

  // Handle call cancellation
  const handleCallCancel = useCallback((...args: any[]) => {
    log.debug("[CALL] handleCallCancel", args);
    setCurrentCallConfigurationState("canceled");
    setCallTextStatus(() => "canceled");
    resetData();
  }, []);

  const handleCallError = useCallback((...args: any[]) => {
    log.debug("[CALL] handleCallError", args);
    setCurrentCallConfigurationState("errored");
    setCallTextStatus(() => "errored");
    resetData();
  }, []);

  const handleCallRejected = useCallback((...args: any[]) => {
    log.debug("[CALL] handleCallRejected", args);
    setCurrentCallConfigurationState("rejected");
    setCallTextStatus(() => "rejected");
    resetData();
  }, []);

  const handleDeviceError = useCallback((...args: any[]) => {
    log.debug("[CALL] handleDeviceError", args);
    setCurrentCallConfigurationState("errored");
    setCallTextStatus(() => "errored");
    resetData();
  }, []);

  const handleDeviceRegistered = useCallback((...args: any[]) => {
    log.debug("[CALL] handleDeviceRegistered", args);
    setCurrentCallConfigurationState("ringing");
    setCallTextStatus(() => "ringing");
  }, []);

  const handleDeviceDisconnected = useCallback(
    (...args: any[]) => {
      log.debug("[CALL] handleDeviceDisconnected", args);
      setCurrentCallConfigurationState("disconnected");
      setCallTextStatus(() => "disconnected");
      resetData();
    },
    [setCurrentCallConfigurationState],
  );

  const registerCallListeners = useCallback(
    (call: Call) => {
      call.on("accept", handleCallAccept);
      call.on("cancel", handleCallCancel);
      call.on("disconnect", handleCallDisconnect);
      call.on("reject", handleCallRejected);
      call.on("error", handleCallError);
    },
    [handleCallAccept, handleCallCancel, handleCallDisconnect, handleCallError, handleCallRejected],
  );

  const registerDeviceListeners = useCallback(
    (device: Device) => {
      device.on("error", handleDeviceError);
      device.on("registered", handleDeviceRegistered);
      device.on("disconnected", handleDeviceDisconnected);
    },
    [handleDeviceError, handleDeviceRegistered, handleDeviceDisconnected],
  );

  // Handle incoming call
  const handleIncomingCall = useCallback(
    (call: Call) => {
      log.debug("[CALL] handleIncomingCall", call);
      setCurrentCall(call);
      registerCallListeners(call);
      call.accept();
    },
    [registerCallListeners],
  );

  // Initialize a call
  const initializeCall = useCallback(async () => {
    const { fromAlias, fromAccountId, fromToken, toAlias, toAccountId, toToken } = currentCallConfiguration;

    // Create a new device instance
    const newDevice = new Device(fromToken, {
      enableImprovedSignalingErrorPrecision: true,
      codecPreferences: ["opus", "pcmu"] as any,
    });

    registerDeviceListeners(newDevice);

    setDevice(newDevice);
    await newDevice.register();

    const call = await newDevice.connect({
      params: {
        To: toAlias,
      },
    });

    setCurrentCall(call);
    registerCallListeners(call);
  }, [registerDeviceListeners, registerCallListeners]);

  const createCallAndListen = useCallback(
    async ({ fromAccountId, toAccountId, feedId }: { fromAccountId: string; toAccountId: string; feedId: string }) => {
      log.debug("[CALL] createCallAndListen", { fromAccountId, toAccountId });
      const callId = cuid();
      const callConfiguration = await createCall(transport, {
        callId,
        fromAccountId,
        toAccountId,
        feedId,
      });

      if (!callConfiguration) {
        console.error("[CALL] call could not be created");
        resetData();
        return Promise.reject("[CALL] call could not be created");
      }

      const callConfigurationRecords = await createCallConfiguration(callConfiguration, "ringing");
      if (!callConfigurationRecords?.length) {
        return;
      }

      currentCallConfiguration = callConfigurationRecords[0] as CallConfigurationRecord;
      await listenForCalls();

      await createCallEvent(transport, { callId, callStatus: "ringing" });
    },
    [boostCallAudio],
  );

  const listenForCalls = useCallback(async () => {
    const { toToken } = currentCallConfiguration;
    log.debug("[CALL] Listening for calls", currentCallConfiguration);

    // Create a new device instance
    const newDevice = new Device(toToken, {
      enableImprovedSignalingErrorPrecision: true,
      codecPreferences: ["opus", "pcmu"] as any,
    });

    await newDevice.audio.setAudioConstraints({
      autoGainControl: false,
      noiseSuppression: false,
      echoCancellation: false,
    });

    const processor = new AudioBoostProcessor({
      gain: 3.5,
      frequency: { highPassFilter: 400, lowPassFilter: 10000 },
      compressionStart: { threshold: -40, knee: 30, ratio: 1, attack: 0.01, release: 0.25 },
      compressionFinish: { threshold: -2, knee: 2, ratio: 20, attack: 0.005, release: 0.3 },
    });

    log.debug("[CALL] boostCallAudio", { boostCallAudio });

    if (boostCallAudio) {
      log.debug("[CALL] Boosting Call Audio");
      await newDevice.audio.addProcessor(processor);
      globalThis.audioBoostProcessor = processor;
    }

    newDevice.on("incoming", handleIncomingCall);
    newDevice.on("error", handleDeviceError);
    newDevice.on("registered", handleDeviceRegistered);
    newDevice.on("disconnected", handleDeviceDisconnected);

    setDevice(newDevice);
    await newDevice.register();
  }, [handleIncomingCall, boostCallAudio]);

  const disconnectCall = useCallback(
    async (state?: CallConfigurationState, duration?: number) => {
      if (currentCall) {
        log.debug("[CALL] disconnecting call");
        currentCall.disconnect();
      }
      if (state) {
        setCurrentCallConfigurationState(state, duration);
      }
      device?.disconnectAll();
      await device?.unregister();
      device?.destroy();
      globalThis.audioBoostProcessor = undefined;
      resetData();
    },
    [currentCall, device],
  );

  useEffect(() => {
    return () => {
      if (device) {
        log.debug("[CALL] cleaning up device on unmount");
        device?.destroy();
      }
    };
  }, [device]);

  const value = {
    callTextStatus,
    currentCall,
    createCallAndListen,
    listenForCalls,
    disconnectCall,
    callStartTime,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

// Custom hook to use the call context
export function useCall() {
  const context = useContext(CallContext);
  if (context === undefined) {
    throw new Error("useCall must be used within a CallProvider");
  }
  return context;
}
